// ═════════════════════════════════════════════════════════════════════════════
// SITE E APP SÃO DOMÍNIOS DIFERENTES (decisão de 26/09/2026).
//
//   cresceja.com.br      → só a landing e a Política de Privacidade.
//   app.cresceja.com.br  → o sistema inteiro (login, cadastro, telas).
//
// O nginx já manda para o app qualquer caminho do site que não seja página do site.
// Mas navegação DENTRO da SPA (um <Link>) não passa pelo nginx: sem estas regras, o
// "Entrar" da landing levava o visitante a usar o sistema em cresceja.com.br — com
// login e preferências separados dos do app, porque o navegador guarda por domínio.
//
// Em desenvolvimento (localhost) site e app são o mesmo endereço, e tudo fica relativo.
// ═════════════════════════════════════════════════════════════════════════════

/** Caminhos que pertencem ao SITE. Todo o resto é do app. */
// /clinicas (21/10/2026): a landing para anúncio de clínica (pages/landing/variante.js).
export const PAGINAS_DO_SITE = Object.freeze(["/", "/privacidade", "/clinicas"]);

/**
 * Este host é a porta do app?
 *
 * `REACT_APP_HOSTS_DO_APP` aceita uma lista separada por vírgula, para um domínio
 * próprio de cliente ou um ambiente de homologação sem precisar de deploy de código.
 * Sem a variável, vale a convenção: host que começa com `app.`.
 */
export function ehPortaDoApp(hostname, lista = process.env.REACT_APP_HOSTS_DO_APP) {
  const host = String(hostname || "").toLowerCase();
  const declarados = String(lista || "")
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  if (declarados.length) return declarados.includes(host);
  return host.startsWith("app.");
}

/** Desenvolvimento: localhost, IP ou nome sem ponto — site e app no mesmo endereço. */
export function ehLocal(hostname) {
  const host = String(hostname || "").toLowerCase();
  return !host.includes(".") || host === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
    || host.endsWith(".localhost");
}

const hostAtual = () => (typeof window !== "undefined" ? window.location.hostname : "");

/**
 * Endereço de uma tela do sistema, a partir de onde a pessoa está.
 *
 * No app ou em desenvolvimento: o próprio caminho (navegação normal da SPA).
 * No site: o endereço completo no app — `REACT_APP_URL_DO_APP`, ou `app.` + o domínio.
 */
export function enderecoDoApp(caminho, hostname = hostAtual(), base = process.env.REACT_APP_URL_DO_APP) {
  if (ehPortaDoApp(hostname) || ehLocal(hostname)) return caminho;
  const raiz = base
    ? String(base).replace(/\/+$/, "")
    : `https://app.${String(hostname).toLowerCase().replace(/^www\./, "")}`;
  return raiz + caminho;
}

/**
 * Para onde mandar quem está no SITE numa tela do app — ou null se está no lugar certo.
 * `caminhoCompleto` inclui busca e âncora (ex.: "/orgs/new?x=1#y").
 */
export function destinoNoApp(caminhoCompleto, hostname = hostAtual(), base) {
  if (ehPortaDoApp(hostname) || ehLocal(hostname)) return null;
  const caminho = String(caminhoCompleto || "/").split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  if (PAGINAS_DO_SITE.includes(caminho)) return null;
  return enderecoDoApp(caminhoCompleto, hostname, base);
}

/** O endereço é de outro domínio (vira <a href>, não <Link>)? */
export const ehEnderecoExterno = (destino) => /^https?:\/\//i.test(String(destino || ""));

/**
 * Endereço de uma página do SITE (landing), a partir de onde a pessoa está.
 *
 * No site e em desenvolvimento: o próprio caminho. No app: o mesmo domínio sem o `app.`
 * — ou `REACT_APP_URL_DO_SITE`. Usado pelo cadastro para mandar à escolha de plano
 * (a seção Planos da landing, decisão de 26/09/2026).
 */
export function enderecoDoSite(caminho, hostname = hostAtual(), base = process.env.REACT_APP_URL_DO_SITE) {
  if (ehLocal(hostname) || !ehPortaDoApp(hostname)) return caminho;
  const raiz = base
    ? String(base).replace(/\/+$/, "")
    : `https://${String(hostname).toLowerCase().replace(/^app\./, "")}`;
  return raiz + caminho;
}

/** Onde se escolhe o plano: a seção Planos da landing. */
export const ESCOLHA_DE_PLANO = "/#planos";
